import React, { useEffect, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useLayout } from '~hooks/useLayout';
import { useSeo } from '~hooks/useSeo';
import { useLocationDetails } from '~hooks/useLocationDetails';

import ServerContext from '~src/serverContext';
import * as REST from '~services/rest';
import LocationContext from '~containers/workers/locationContext';
import WorkersList from '~containers/workers/list';
import Stars from '~components/stars';
import Breadcrumbs from './breadcrumbs';
import FAQ from './faq';
import CitySeoContent from './seo-content';
import CTA from './cta';
import { resolveCityState } from './seo-content/sections';

// import { FaStar } from "react-icons/fa";

const City = ({ location }) => {
  const intl = useIntl();
  const urlLocation = useLocation();
  const cache = useContext(ServerContext);
  const [isMapView, setIsMapView] = useState(false);

  // 0-ad state: server-side from the worker list ([] is authoritative), client-side
  // from the hydrated flag. Snapshotted once per mount so cleanup cannot flip it.
  const ssrWorkers = cache.get(`cityWorkers${location?.slug || ''}`);
  const [isZeroAds] = useState(() => resolveCityState(ssrWorkers) === 'zero' || cache.get('cityIsZeroAds') === true);

  // A client-side navigation gets no hydrated city state and fetches the content itself.
  const [fromServer] = useState(() => ssrWorkers !== undefined || cache.get('cityIsZeroAds') !== undefined);
  const [aiContent, setAiContent] = useState(() => cache.get('cityAiContent') || null);

  // Drop the hydrated keys after the snapshots above consume them, so a client-side
  // navigation cannot carry this city's state into the next. Must live here: the
  // route container mounts this page through an async slug check and would race.
  useEffect(() => {
    cache.del('cityIsZeroAds');
    cache.del('cityAiContent');
  }, []);

  useEffect(() => {
    if (fromServer || !location?.slug) return undefined;

    let cancelled = false;
    // Absent content is normal (translated copy is the fallback), so failures stay silent.
    REST.get({
      name: 'cities/ai-content',
      params: { city_slug: location.slug, published_only: true },
      shouldThrowError: true,
    })
      .then((response) => {
        if (!cancelled) {
          setAiContent(response?.data?.content || null);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [location?.slug]);

  const { locationDetails, pricingDetails, isLoading, locationTouched, requestLocationByID } = useLocationDetails(
    location?.locationId,
    [urlLocation.pathname]
  );

  // Add effect to track location changes and manually trigger requestLocationByID
  useEffect(() => {
    if (location?.locationId) {
      requestLocationByID(location.locationId);
    }
  }, [location?.locationId]);

  useLayout(
    {
      displayCities: !isMapView,
      displayNeighborhoods: !isMapView,
      displayFooter: !isMapView,
      displaySignIn: true,
      displayTopCTABar: true,
      logoToBottom: true,
      locationId: location?.locationId,
      isCityPage: true,
    },
    [isMapView]
  );

  useSeo(
    intl.formatMessage(
      {
        id: 'containers.city.seo.title',
      },
      { city: location?.description }
    )
  );

  const handleMapViewChange = (isShowingMap) => {
    setIsMapView(isShowingMap);
  };

  return (
    <LocationContext.Provider
      value={{
        locationDetails,
        pricingDetails,
        initialSearchLocationId: location?.locationId,
        isLoadingLocation: isLoading,
        locationTouched,
        requestLocationByID,
      }}
    >
      <Breadcrumbs city={location?.description} slug={location?.slug} />
      <section
        className={`c-page l-container c-page--matches c-page-loder-hight${isZeroAds ? ' c-page--zero-ads' : ''}`}
      >
        {/* City SEO content renders in every lifecycle state so the H1 and
            localized copy are in the initial SSR HTML before hydration. */}
        <CitySeoContent city={location?.description} isZeroAds={isZeroAds} aiContent={isZeroAds ? aiContent : null} />

        {isZeroAds ? (
          // 0-ad state: SEO content + a prominent intake CTA only. No results
          // list, no scarcity wording, no cities from elsewhere.
          <CTA city={location?.description} locationId={location?.locationId} />
        ) : location?.locationId ? (
          <>
            <a
              className="c-star__fc-link"
              href="https://www.feedbackcompany.com/nl-nl/reviews/juan-pescador/"
              rel="noopener noreferrer nofollow"
              aria-label="Feedback Company"
              target="_blank"
            >
              <Stars value={4} />
            </a>
            <WorkersList isCityPage onMapViewChange={handleMapViewChange} />
          </>
        ) : null}

        {location?.description ? <FAQ city={location.description} /> : null}
      </section>
    </LocationContext.Provider>
  );
};

export default City;
